/**
 * The canvas side column's sections (RFC 0273 S2/S3b).
 *
 * Two kinds of section, and the difference is who owns the relation being shown.
 *
 * COMMENTS is generic: any document can carry them, the host owns the target
 * shape, and the projection is the same on every surface. So the host renders
 * it, and it needs no declaration from anyone.
 *
 * An APP section shows a relation only that app understands ("the sessions
 * relevant to this review"), which the host cannot compute and no item
 * vocabulary can express. So the app renders it, in its own frame, by mounting
 * its EXISTING view document with `mod.surface.name === 'panel'` and branching.
 * One artifact, one branch, not a second document to keep in step.
 *
 * The switcher IS the panel header rather than a strip below it: a column with
 * a "Comments" title above a Comments/Sessions tab row states the same thing
 * twice and lies whenever Sessions is selected. Split into tabs + body so the
 * caller can put each where the panel wants them.
 */

import type { AppSummary } from '@mod/core/model'
import { CodeFileHistory } from '../../features/code/CodeFileHistory'
import { CommentsSection } from '../../features/documents/CommentsSection'
import { DocumentView } from '../../features/documents/DocumentView'
import { HistorySection } from '../../features/documents/HistorySection'
import type { VersionPreview } from '../../features/documents/VersionPreview'
import { isCodeId } from '../../services/code/codeFile'
import type { Subject } from './subject'

/** The comments section is always present and always first: it is the column's
 *  own reason for existing, and an app should not be able to displace it. */
export const COMMENTS_ID = '__comments'

/** History is host-owned like Comments, and always second (RFC 0313 3g): it
 *  is added to the list and displaces nothing. */
export const HISTORY_ID = '__history'

export interface SideSection {
  id: string
  title: string
}

/** Comments, History, then whatever the open app declares. Pure, so the
 *  header and the body agree on the list without either owning it. A
 *  project file's History is its git log (CodeFileHistory). */
export function sideSections(
  app?: AppSummary,
  opts: { history?: boolean } = {},
): SideSection[] {
  return [
    { id: COMMENTS_ID, title: 'Comments' },
    ...(opts.history ? [{ id: HISTORY_ID, title: 'History' }] : []),
    ...(app?.panels ?? [])
      // Panel-slot views only. A host projection (`items`) draws elsewhere;
      // a panel saved before `kind` existed is a view, so it stays.
      .filter(
        (p) =>
          (p.slot === undefined || p.slot === 'panel') && p.kind !== 'items',
      )
      .map((p) => ({ id: p.id, title: p.title })),
  ]
}

/**
 * The active section, resolved against what currently exists.
 *
 * Derived rather than corrected in an effect: a section can stop existing
 * (the canvas changed to a plain document, or the app dropped its declaration)
 * and an effect would leave one render pointing at a section that is gone.
 */
export function resolveSection(
  selected: string,
  sections: SideSection[],
): string {
  return sections.some((s) => s.id === selected) ? selected : COMMENTS_ID
}

/** The panel header: a tab per section, or nothing to switch between. */
export function SidePanelTabs({
  sections,
  active,
  onSelect,
}: {
  sections: SideSection[]
  active: string
  onSelect: (id: string) => void
}) {
  // One section is not a choice. Render the plain title the column has always
  // had, so a plain document looks exactly as it did before sections existed.
  if (sections.length <= 1) {
    return (
      <span className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
        {sections[0]?.title ?? 'Comments'}
      </span>
    )
  }
  return (
    <div className="flex min-w-0 items-center gap-1">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect(s.id)}
          aria-current={active === s.id ? 'page' : undefined}
          className={`shrink-0 rounded px-1.5 py-0.5 font-medium text-[10px] uppercase tracking-wide ${
            active === s.id
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground/70 hover:bg-muted/60 hover:text-foreground'
          }`}
        >
          {s.title}
        </button>
      ))}
    </div>
  )
}

/** The panel body: whichever section is active. */
export function SidePanelBody({
  active,
  file,
  projectId,
  subject,
  include = [],
  onOpenSession,
  onGoToPassage,
  onOpenDocument,
  onViewVersion,
  viewingVersionId,
}: {
  /** Open a document or a code file on the canvas. */
  onOpenDocument?: (id: string) => void
  /** Show a saved version in the document's pane (RFC 0313 3c). */
  onViewVersion?: (preview: VersionPreview | null) => void
  viewingVersionId?: string | null
  /** Scroll the document beside the column to a passage (RFC 0324 3e). */
  onGoToPassage?: (anchorText: string) => void
  active: string
  file: Subject
  projectId: string
  subject: string | null
  /** Extra documents whose comments belong to this surface (RFC 0273 §5). */
  include?: string[]
  onOpenSession?: (id: string) => void
}) {
  if (active === COMMENTS_ID) {
    return (
      <CommentsSection
        projectId={projectId}
        documentId={subject ?? file.id}
        include={include}
        {...(onGoToPassage ? { onGoToPassage } : {})}
      />
    )
  }
  if (active === HISTORY_ID && isCodeId(file.id)) {
    return (
      <CodeFileHistory
        projectId={projectId}
        codeId={file.id}
        {...(onOpenDocument ? { onOpen: onOpenDocument } : {})}
      />
    )
  }
  if (active === HISTORY_ID) {
    return (
      <HistorySection
        projectId={projectId}
        documentId={file.id}
        {...(onOpenSession ? { onOpenSession } : {})}
        {...(onOpenDocument ? { onOpenDocument } : {})}
        {...(onViewVersion ? { onViewVersion, viewingVersionId } : {})}
      />
    )
  }
  // The SAME view document as the canvas, mounted a second time and told it is
  // the panel. It gets no onSubject: the canvas copy owns the subject, and
  // letting the panel copy set it too would make two frames fight over what the
  // column is scoped to. Mounted only while selected, so an app that has not
  // branched yet cannot run a full canvas boot in a 256px column on load.
  return (
    <DocumentView
      file={file}
      projectId={projectId}
      onOpenSession={onOpenSession}
      surface="panel"
    />
  )
}
